import { evaluateMeasurement } from '@felix/domain';
import type { StatusTone } from '../../StatusBadge.js';
import type {
  CmReport,
  LimitsView,
  Measurement,
  MeasurementEntry,
  MeasurementPoint,
  MeasurementResult,
} from './condition-types.js';
import type { JobView } from './types.js';

/**
 * Durum izleme ekranlarının saf hesapları (plan M7 Task 21a; ADR 0029 K18–K21): limit metni,
 * canlı sonuç (domain değerlendirmesi), iş emrinin noktaları, limit değişikliği işareti, çizgi
 * grafiğin geometrisi. Sunucu aynı kuralları yeniden uygular; ekran yalnız erken söyler.
 */

const numberFormats = new Map<string, Intl.NumberFormat>();

/** Dile göre sayı: en çok üç ondalık, binlik ayraçsız (ölçüm değeri). */
export function measureNumber(language: string, value: number): string {
  const locale = language === 'tr' ? 'tr-TR' : 'en-US';
  let format = numberFormats.get(locale);
  if (!format) {
    format = new Intl.NumberFormat(locale, { maximumFractionDigits: 3, useGrouping: false });
    numberFormats.set(locale, format);
  }
  return format.format(value);
}

/**
 * Limit metni (PMS belgesi §11; K18): iki yan dahilse "400–450 °C"; tek yan ya da hariç sınır
 * ">3.5 bar", "≤7 mm/s"; iki yan farklıysa "> a · ≤ b". Sınır yoksa "—".
 */
export function limitLabel(
  limits: Pick<LimitsView, 'lowInclusive' | 'highInclusive'> & {
    alertLow: number | null;
    alertHigh: number | null;
  },
  unit: string,
  language: string,
): string {
  const n = (value: number) => measureNumber(language, value);
  const { alertLow: low, alertHigh: high } = limits;
  if (low === null && high === null) return '—';
  if (low !== null && high !== null && limits.lowInclusive && limits.highInclusive) {
    return `${n(low)}–${n(high)} ${unit}`;
  }
  const parts: string[] = [];
  if (low !== null) parts.push(`${limits.lowInclusive ? '≥' : '>'}${n(low)}`);
  if (high !== null) parts.push(`${limits.highInclusive ? '≤' : '<'}${n(high)}`);
  return `${parts.join(' · ')} ${unit}`;
}

/** Tehlike sınırının metni (FAIL sınırı); yoksa "—". */
export function dangerLabel(limits: LimitsView, unit: string, language: string): string {
  return limitLabel(
    {
      alertLow: limits.dangerLow,
      alertHigh: limits.dangerHigh,
      lowInclusive: limits.lowInclusive,
      highInclusive: limits.highInclusive,
    },
    unit,
    language,
  );
}

/** Canlı sonuç: domain'in `evaluateMeasurement`'ı; geçersiz değerde `null`. */
export function evaluate(value: number, limits: LimitsView): MeasurementResult | null {
  if (!Number.isFinite(value)) return null;
  try {
    return evaluateMeasurement(value, limits);
  } catch {
    return null;
  }
}

/** Ekrandaki ad: normal → PASS, abnormal → ABNORMAL, fail → FAIL. */
export function resultKey(result: string): 'pass' | 'abnormal' | 'fail' {
  if (result === 'fail') return 'fail';
  if (result === 'abnormal') return 'abnormal';
  return 'pass';
}

export function resultTone(result: string): StatusTone {
  if (result === 'fail') return 'danger';
  if (result === 'abnormal') return 'warning';
  return 'success';
}

const live = (p: MeasurementPoint) => p.deletedAt === null && p.active;

/**
 * İş emrinin Measurements adımındaki noktalar: planlı işte işin noktaları ve tetikleyici noktalar
 * (sıra korunur); tetikleyici noktalar raporda zorunludur (`PARAMETER_REQUIRED`; ADR 0029 C3).
 * Plansız iş emrinde ekipmanın noktaları, hiçbiri zorunlu değil.
 */
export function pointsForWorkOrder(
  points: readonly MeasurementPoint[],
  wo: { kind: string; equipmentId: string },
  job: Pick<JobView, 'id' | 'interval'> | null,
): { points: MeasurementPoint[]; required: Set<string> } {
  if (job) {
    const triggers = new Set(job.interval.conditionPointIds);
    return {
      points: points.filter((p) => live(p) && (p.jobDefinitionId === job.id || triggers.has(p.id))),
      required: new Set(points.filter((p) => live(p) && triggers.has(p.id)).map((p) => p.id)),
    };
  }
  return {
    points: points.filter((p) => live(p) && p.equipmentId === wo.equipmentId),
    required: new Set(),
  };
}

/** Değeri girilmemiş zorunlu noktalar. */
export function missingParameters(
  required: ReadonlySet<string>,
  entries: readonly MeasurementEntry[],
): string[] {
  const given = new Set(entries.map((e) => e.pointId));
  return [...required].filter((id) => !given.has(id));
}

/**
 * Measurements adımının kipi. Yeni raporda değerler girilir ve tetikleyici noktalar zorunludur.
 * Raporlanmış kayıtta kayıtlı ölçümler gösterilir. Düzeltmede (K14) ilk raporun ölçümleri geçerli
 * kalır (değiştirilemez satırlar; ADR 0029 Ek S35): adım salt okunur, zorunluluk aranmaz ve
 * düzeltmeyle ikinci okuma gönderilmez; aynı iş emrinin çift okuması trendi bozar [UR Z27 2.2.4].
 */
export function measurementStepMode(input: {
  editable: boolean;
  recorded: boolean;
  correcting: boolean;
}): { editable: boolean; showRecorded: boolean; enforceRequired: boolean } {
  if (input.correcting) return { editable: false, showRecorded: true, enforceRequired: false };
  return {
    editable: input.editable,
    showRecorded: input.recorded,
    enforceRequired: input.editable,
  };
}

const snapshotKey = (m: Measurement) => JSON.stringify(m.limitsSnapshot ?? null);

/**
 * Limiti değişmiş ölçümler [UR Z27 2.2.4]: aynı noktanın önceki (iptal edilmemiş) ölçümünden
 * farklı limitle değerlendirilmiş olanlar. Limit değişikliği ölçümün anlık kopyasından türetilir.
 */
export function limitChangeIds(measurements: readonly Measurement[]): Set<string> {
  const changed = new Set<string>();
  const previous = new Map<string, string>();
  const ordered = [...measurements]
    .filter((m) => !m.voided)
    .sort((a, b) => a.measuredAt.localeCompare(b.measuredAt) || a.id.localeCompare(b.id));
  for (const m of ordered) {
    const key = snapshotKey(m);
    const before = previous.get(m.pointId);
    if (before !== undefined && before !== key) changed.add(m.id);
    previous.set(m.pointId, key);
  }
  return changed;
}

export interface Sparkline {
  path: string;
  dots: { id: string; x: number; y: number; result: string }[];
  /** Uyarı bandı (normal aralık) y koordinatları; sınır yoksa `null`. */
  band: { y1: number; y2: number } | null;
}

/**
 * Küçük çizgi grafik (dataviz: tek seri, eksensiz, normal aralık bandı): iptal edilmemiş ölçümler
 * zamana göre; y alanı değerleri ve uyarı sınırlarını kapsar. Koordinatlar `width × height` içinde.
 */
export function sparkline(
  measurements: readonly Measurement[],
  limits: LimitsView,
  size: { width: number; height: number; pad?: number },
): Sparkline {
  const pad = size.pad ?? 3;
  const series = [...measurements]
    .filter((m) => !m.voided && Number.isFinite(m.value))
    .sort((a, b) => a.measuredAt.localeCompare(b.measuredAt) || a.id.localeCompare(b.id));
  if (series.length === 0) return { path: '', dots: [], band: null };
  const times = series.map((m) => Date.parse(m.measuredAt));
  const bounds = [limits.alertLow, limits.alertHigh].filter((v): v is number => v !== null);
  const values = [...series.map((m) => m.value), ...bounds];
  let min = Math.min(...values);
  let max = Math.max(...values);
  if (min === max) {
    min -= 1;
    max += 1;
  }
  const t0 = Math.min(...times);
  const t1 = Math.max(...times);
  const x = (t: number) =>
    t1 === t0 ? size.width / 2 : pad + ((t - t0) / (t1 - t0)) * (size.width - 2 * pad);
  const y = (v: number) => pad + (1 - (v - min) / (max - min)) * (size.height - 2 * pad);
  const dots = series.map((m, i) => ({
    id: m.id,
    x: round(x(times[i] as number)),
    y: round(y(m.value)),
    result: String(m.result),
  }));
  const path = dots.map((d, i) => `${i === 0 ? 'M' : 'L'}${d.x} ${d.y}`).join(' ');
  const band =
    limits.alertLow === null && limits.alertHigh === null
      ? null
      : {
          y1: round(y(limits.alertHigh ?? max)),
          y2: round(y(limits.alertLow ?? min)),
        };
  return { path, dots, band };
}

const round = (value: number) => Math.round(value * 10) / 10;

/** CM önizlemesi uygulanabilir mi: satır var ve satır hatası yok (kısmi içe aktarma yok; C10). */
export function cmCanApply(report: CmReport): boolean {
  return report.rowsTotal > 0 && report.errors.length === 0;
}
